// Quiet reload for the client portal and public pre-sign-in routes (DSAF-05,
// D-16). Clients never see a banner, overlay, toast, or any message about a
// new version: when a newer build is detected the page simply reloads, but only
// at a moment the person has already moved on (the next visibility change to
// visible, window focus, or router navigation), never while the page sits
// visible and idle, and never while a portal write is in flight.
//
// Pre-sign-in pages (signup, welcome, accept-invite, login, help) hold typed
// input in plain forms, and "switch to the email tab to copy a code, then come
// back" is their common flow. There a pending reload waits for a router
// navigation only; focus and visibility still look for a newer build but never
// reload. The client portal keeps the D-16 behavior.
//
// This hook reads no Firestore and renders nothing. It imports only
// fetchLiveManifest from useVersionWatch, so it reuses the same validated
// manifest fetch without mounting the agent store. Automatic reloads go through
// the reload-loop guard (noteAutoReload and reloadLoopTripped) so a build that
// keeps disagreeing with itself cannot spin the page.
import React from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import buildInfo from '../buildInfo.json';
import { onClientTooOld } from '../utils/appCallable';
import {
    POLL_INTERVAL_MS,
    MIN_FETCH_GAP_MS,
    localBuildFrom,
    noteAutoReload,
    reloadLoopTripped,
} from '../utils/versionWatch';
import { fetchLiveManifest } from './useVersionWatch';

let busyCount = 0;

export function beginQuietReloadBusy() {
    busyCount += 1;
}

export function endQuietReloadBusy() {
    busyCount = Math.max(0, busyCount - 1);
}

const isPortalPath = (pathname) => /^\/portal(\/|$)/i.test(pathname || '');

const getStorage = () => {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
};

export function useQuietReload({
    reloadOnClientTooOld = false,
    reload = () => window.location.reload(),
} = {}) {
    const location = useLocation();
    const reloadRef = React.useRef(reload);
    reloadRef.current = reload;
    const pendingRef = React.useRef(false);
    const firstLocationRef = React.useRef(true);
    const portalRef = React.useRef(isPortalPath(location.pathname));
    portalRef.current = isPortalPath(location.pathname);

    const doReload = React.useCallback((requireIdle) => {
        if (requireIdle && busyCount > 0) return;
        const storage = getStorage();
        const now = Date.now();
        if (storage && reloadLoopTripped(storage, now)) return;
        if (storage) noteAutoReload(storage, now);
        try {
            if (typeof Sentry.addBreadcrumb === 'function') {
                Sentry.addBreadcrumb({ category: 'deploy-safety', level: 'info', message: 'quiet reload' });
            }
        } catch {
            // telemetry must never block the reload
        }
        reloadRef.current();
    }, []);

    React.useEffect(() => {
        let cancelled = false;
        let lastPollAt = null;
        // Focus and visibilitychange usually fire together; a moment inside the
        // fetch gap reuses the last answer. The mount and the interval always fetch.
        const poll = async ({ force = false } = {}) => {
            const at = Date.now();
            if (!force && lastPollAt !== null && at - lastPollAt < MIN_FETCH_GAP_MS) return;
            lastPollAt = at;
            const live = await fetchLiveManifest({});
            if (cancelled || !live) return;
            if (live.manifest.builtAt !== localBuildFrom(buildInfo)) pendingRef.current = true;
        };
        // A pending reload fires only on a person-driven moment, and on a
        // pre-sign-in page only on navigation; otherwise the moment is a chance
        // to look for a newer build.
        const onMoment = () => {
            if (pendingRef.current && portalRef.current) doReload(true);
            else if (!pendingRef.current) poll();
        };
        const onVisibility = () => {
            if (document.visibilityState === 'visible') onMoment();
        };

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', onMoment);
        const interval = setInterval(() => poll({ force: true }), POLL_INTERVAL_MS);
        const unsub = reloadOnClientTooOld ? onClientTooOld(() => doReload(false)) : null;
        poll({ force: true });

        return () => {
            cancelled = true;
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', onMoment);
            clearInterval(interval);
            if (unsub) unsub();
        };
    }, [reloadOnClientTooOld, doReload]);

    React.useEffect(() => {
        if (firstLocationRef.current) {
            firstLocationRef.current = false;
            return;
        }
        if (pendingRef.current) doReload(true);
    }, [location.key, location.pathname, doReload]);
}
